import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../api";
import CustomerShell from "../components/customer/CustomerShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Link } from "react-router-dom";
import { MessageSquare, Plus, Send, ArrowLeft } from "lucide-react";

export default function Support() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: "",
    category: "",
    priority: "medium",
    message: ""
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const res = await api.get("/tickets");
      setTickets(res.data);
    } catch (err) {
      console.error("Tickets error:", err);
    } finally {
      setLoading(false);
    }
  };

  const createTicket = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", newTicket);
      setNewTicket({ subject: "", category: "", priority: "medium", message: "" });
      setShowNewTicket(false);
      fetchTickets();
      alert("Support-Ticket wurde erfolgreich erstellt!");
    } catch (err) {
      alert("Ticket konnte nicht erstellt werden: " + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return <CustomerShell active="support"><div className="py-20 text-center text-slate-400">Support-Tickets werden geladen...</div></CustomerShell>;
  }

  return (
    <CustomerShell active="support">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-white">Support-Anfragen</h1>
            <p className="text-sm text-slate-400 mt-1">Unser 24/7-Supportteam unterstützt Sie gerne bei technischen Fragen, Abrechnung oder Domains.</p>
          </div>
          <Button onClick={() => setShowNewTicket(true)} className="rounded-xl bg-[#E63946] hover:bg-[#d02f3c] text-white font-bold shadow-md">
            <Plus className="h-4 w-4 mr-2" />
            Neues Ticket erstellen
          </Button>
        </div>

        {showNewTicket && (
          <div className="rounded-[22px] border border-white/10 bg-[#0F1422] p-6 sm:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.3)]">
            <h2 className="text-lg font-bold text-white mb-4">Neues Support-Ticket erstellen</h2>
            <form onSubmit={createTicket} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">Betreff</label>
                  <input
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                    required
                    placeholder="Kurze Beschreibung Ihres Problems"
                    className="w-full rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm font-medium text-white focus:bg-white/10 focus:border-[#FF7A00] outline-none transition placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 mb-1.5">Kategorie</label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                    required
                    className="w-full rounded-xl border border-white/10 bg-[#0F1422] px-3.5 py-2.5 text-sm font-medium text-white focus:border-[#FF7A00] outline-none transition"
                  >
                    <option value="" disabled>Kategorie auswählen</option>
                    <option value="technical">Technisches Problem</option>
                    <option value="billing">Abrechnung</option>
                    <option value="account">Konto</option>
                    <option value="domain">Domain</option>
                    <option value="other">Sonstiges</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1.5">Priorität</label>
                <select
                  value={newTicket.priority}
                  onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                  className="w-full rounded-xl border border-white/10 bg-[#0F1422] px-3.5 py-2.5 text-sm font-medium text-white focus:border-[#FF7A00] outline-none transition"
                >
                  <option value="low">Niedrig</option>
                  <option value="medium">Normal</option>
                  <option value="high">Hoch</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1.5">Nachricht</label>
                <textarea
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({...newTicket, message: e.target.value})}
                  required
                  rows={5}
                  placeholder="Beschreiben Sie Ihr Problem detailliert..."
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm font-medium text-white focus:bg-white/10 focus:border-[#FF7A00] outline-none transition placeholder:text-slate-500"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="submit" className="rounded-xl bg-[#E63946] hover:bg-[#d02f3c] text-white font-bold">
                  <Send className="h-4 w-4 mr-2" />
                  Ticket erstellen
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowNewTicket(false)} className="rounded-xl border-white/10 bg-white/5 text-white hover:bg-white/10">
                  Abbrechen
                </Button>
              </div>
            </form>
          </div>
        )}

        <div className="space-y-4">
          {tickets.length === 0 ? (
            <div className="rounded-[22px] border border-white/10 bg-[#0F1422] p-12 text-center shadow-[0_4px_24px_rgba(0,0,0,0.3)]">
              <MessageSquare className="h-12 w-12 text-slate-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Keine Support-Tickets vorhanden</h3>
              <p className="text-slate-400 text-sm">Sie haben aktuell keine offenen oder geschlossenen Support-Anfragen.</p>
            </div>
          ) : (
            tickets.map((ticket) => (
              <div key={ticket.id} className="rounded-[20px] border border-white/10 bg-[#0F1422] p-6 shadow-sm hover:border-white/20 transition">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-white">{ticket.subject}</h3>
                    <p className="text-xs text-slate-400 mt-1">Ticket #{ticket.id} • {ticket.category}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Erstellt am: {new Date(ticket.createdAt).toLocaleDateString('de-CH')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                      ticket.status === 'open' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                      ticket.status === 'in_progress' ? 'bg-blue-500/10 text-[#0EA5E9] border border-blue-500/20' :
                      ticket.status === 'answered' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                      'bg-white/5 text-slate-400 border border-white/10'
                    }`}>
                      {ticket.status === 'open' ? 'Offen' :
                       ticket.status === 'in_progress' ? 'In Bearbeitung' :
                       ticket.status === 'answered' ? 'Beantwortet' : 'Geschlossen'}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                      ticket.priority === 'high' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                      ticket.priority === 'medium' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                      'bg-white/5 text-slate-400 border border-white/10'
                    }`}>
                      {ticket.priority === 'high' ? 'Hoch' :
                       ticket.priority === 'medium' ? 'Normal' : 'Niedrig'}
                    </span>
                    <Link
                      to={`/tickets/${ticket.id}`}
                      className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white px-3.5 py-1.5 text-xs font-bold transition"
                    >
                      Details anzeigen
                    </Link>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </CustomerShell>
  );
}